import { useCallback, useEffect, useRef, useState } from 'react';
import { ACTIVE_STATUSES, POLL_INTERVAL_MS } from '@/constants';
import type { JobDetails } from '@/types';
import { fetchBulkPromoByCriteria, haveBulkPromosChanged, isJobWithinRecentDays, sortBulkPromos } from '@/utils';
export const useBulkPromoJobs = () => {
  const [bulkPromos, setBulkPromos] = useState<JobDetails[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const bulkPromosRef = useRef<JobDetails[]>([]);
  const updatePromosIfChanged = useCallback((newPromos: JobDetails[]) => {
    const filteredPromos = newPromos.filter(promo => isJobWithinRecentDays(new Date(promo.createdAt)));
    const sortedPromos = sortBulkPromos(filteredPromos);
    if (haveBulkPromosChanged(bulkPromosRef.current, sortedPromos)) {
      bulkPromosRef.current = sortedPromos;
      setBulkPromos(sortedPromos);
    }
  }, []);
  const fetchAndUpdatePromos = useCallback(async (withLoading = true) => {
    try {
      if (withLoading) setIsLoading(true);
      const promos = await fetchBulkPromoByCriteria();
      updatePromosIfChanged(promos);
    } catch (error) {
      console.error('Failed to fetch bulk promos:', error);
    } finally {
      setIsLoading(false);
    }
  }, [updatePromosIfChanged]);
  const pollActivePromos = useCallback(async () => {
    const hasActivePromos = bulkPromosRef.current.some(promo_0 => ACTIVE_STATUSES.includes(promo_0.status));
    if (hasActivePromos) {
      await fetchAndUpdatePromos(false);
    }
  }, [fetchAndUpdatePromos]);
  useEffect(() => {
    fetchAndUpdatePromos();
    const interval = setInterval(async () => {
      try {
        await pollActivePromos();
      } catch (error_0) {
        console.error('Polling error:', error_0);
      }
    }, POLL_INTERVAL_MS);
    return () => {
      clearInterval(interval);
    };
  }, [fetchAndUpdatePromos, pollActivePromos]);
  return {
    bulkPromos,
    isLoading,
    refreshPromos: fetchAndUpdatePromos
  };
};